import { fuelDensityForSpec } from "./aircraftDatabase";
import { blankAircraft, HAWKER, PC12 } from "./airports";
import { HAWKER_800XP, isHawker800xpType } from "./hawker800xp";
import type { AircraftSpec } from "./types";

/**
 * In-app Weight & Balance — Celtic Air desk sheet, not an AFM.
 *
 * Hawker 800XP (no winglets) lb figures come from Emerson’s Weights &
 * Capacities table (same book as `hawker800xp.ts`). Envelope vertices,
 * LEMAC / MAC, and station arms are classic HS.125-800 / 800XP-style
 * **desk training defaults** so the operator can overwrite them with
 * FlightFX / EFB numbers. Do not paste copyrighted AFM pages here.
 */

export type WbPointKind = "zfw" | "ramp" | "tow" | "ldg";

export type WbEnvelopeVertex = {
  weightLb: number;
  pctMac: number;
};

export type WbStation = {
  id: string;
  label: string;
  armIn: number;
  weightLb: number;
};

export type WbSeat = WbStation & {
  occupied: boolean;
  defaultWeightLb: number;
};

export type WbTank = {
  id: string;
  label: string;
  armIn: number;
  capacityLb: number;
  quantityLb: number;
};

export type WbLimits = {
  bowLb: number;
  bowArmIn: number;
  mzfwLb: number;
  mtowLb: number;
  mlwLb: number;
  mrwLb: number;
  maxFuelLb: number;
  ventralFuelLb: number;
  maxPayloadLb: number;
  payloadFullFuelLb: number;
  macIn: number;
  lemacStaIn: number;
  taxiFuelLb: number;
};

export type WbProfile = {
  aircraftId: string;
  icaoType: string;
  sourceLabel: string;
  limits: WbLimits;
  envelope: WbEnvelopeVertex[];
  seats: WbSeat[];
  cargo: WbStation[];
  tanks: WbTank[];
  tripFuelLb: number;
  /** Limit text from the last pax recalc. Empty when that pass was clean. */
  loadFlagNote?: string;
};

export type WeightBalanceDesk = {
  selectedAircraftId: string;
  profiles: Record<string, WbProfile>;
};

export type WbComputedPoint = {
  kind: WbPointKind;
  label: string;
  weightLb: number;
  momentInLb: number;
  armIn: number;
  pctMac: number;
  ok: boolean;
  reasons: string[];
};

export type WbEnvelopeLimits = {
  fwdPctMac: number;
  aftPctMac: number;
  minWeightLb: number;
  maxWeightLb: number;
};

export type WbResult = {
  zfw: WbComputedPoint;
  ramp: WbComputedPoint;
  tow: WbComputedPoint;
  ldg: WbComputedPoint;
  payloadLb: number;
  fuelOnBoardLb: number;
  takeoffFuelLb: number;
  fuelRemainingLb: number;
  withinLimits: boolean;
  reasons: string[];
};

export const EMPTY_WEIGHT_BALANCE: WeightBalanceDesk = {
  selectedAircraftId: "",
  profiles: {},
};

export const WB_DEFAULT_PAX_LB = 200;

/**
 * Typical 800XP wing geometry used by type-course / EFB worksheets.
 * Operators should overwrite with their FlightFX numbers.
 */
export const HAWKER_800XP_MAC_IN = 82.5;
export const HAWKER_800XP_LEMAC_STA_IN = 351.4;

/**
 * Classic 800XP-style CG envelope (weight vs %MAC). Closed polygon,
 * clockwise from low-weight forward. Desk training default — not AFM.
 */
export const HAWKER_800XP_WB_ENVELOPE: readonly WbEnvelopeVertex[] = [
  { weightLb: 15000, pctMac: 18.0 },
  { weightLb: 18450, pctMac: 18.5 },
  { weightLb: 23350, pctMac: 19.8 },
  { weightLb: 28120, pctMac: 21.2 },
  { weightLb: 28120, pctMac: 30.8 },
  { weightLb: 23350, pctMac: 31.4 },
  { weightLb: 18450, pctMac: 31.7 },
  { weightLb: 15000, pctMac: 31.7 },
];

const HAWKER_WING_CAP_LB = Math.round((HAWKER_800XP.maxFuelLb - HAWKER_800XP.ventralFuelLb) / 2);

export function pctMacFromArm(armIn: number, lemacStaIn: number, macIn: number): number {
  if (!(macIn > 0) || !Number.isFinite(armIn) || !Number.isFinite(lemacStaIn)) return 0;
  return ((armIn - lemacStaIn) / macIn) * 100;
}

export function armFromPctMac(pctMac: number, lemacStaIn: number, macIn: number): number {
  if (!Number.isFinite(pctMac) || !Number.isFinite(lemacStaIn) || !Number.isFinite(macIn)) return 0;
  return lemacStaIn + (pctMac / 100) * macIn;
}

export function hawkerBowArmIn(): number {
  return armFromPctMac(26, HAWKER_800XP_LEMAC_STA_IN, HAWKER_800XP_MAC_IN);
}

export function sumMoments(items: ReadonlyArray<{ weightLb: number; armIn: number }>): {
  weightLb: number;
  momentInLb: number;
} {
  let weightLb = 0;
  let momentInLb = 0;
  for (const item of items) {
    const w = finiteNumber(item.weightLb, 0);
    const arm = finiteNumber(item.armIn, 0);
    weightLb += w;
    momentInLb += w * arm;
  }
  return { weightLb, momentInLb };
}

export function cgArmIn(weightLb: number, momentInLb: number): number {
  if (!(weightLb > 0) || !Number.isFinite(momentInLb)) return 0;
  return momentInLb / weightLb;
}

export function formatLb(n: number): string {
  const rounded = Math.round(finiteNumber(n, 0));
  return `${rounded.toLocaleString("en-US")} lb`;
}

export function formatPctMac(n: number): string {
  if (!Number.isFinite(n)) return "—";
  return `${n.toFixed(1)}% MAC`;
}

export function pointInPolygon(
  vertices: ReadonlyArray<{ x: number; y: number }>,
  x: number,
  y: number,
): boolean {
  if (vertices.length < 3) return false;
  let inside = false;
  for (let i = 0, j = vertices.length - 1; i < vertices.length; j = i++) {
    const xi = vertices[i]!.x;
    const yi = vertices[i]!.y;
    const xj = vertices[j]!.x;
    const yj = vertices[j]!.y;
    const denom = yj - yi;
    const intersect = yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (denom === 0 ? Number.EPSILON : denom) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

export function envelopePolygon(envelope: ReadonlyArray<WbEnvelopeVertex>): { x: number; y: number }[] {
  return envelope.map((v) => ({ x: v.pctMac, y: v.weightLb }));
}

export function pointInEnvelope(
  envelope: ReadonlyArray<WbEnvelopeVertex>,
  weightLb: number,
  pctMac: number,
): boolean {
  return pointInPolygon(envelopePolygon(envelope), pctMac, weightLb);
}

/** Forward / aft %MAC at a weight by intersecting the envelope with a horizontal. */
export function envelopeLimitsAtWeight(
  envelope: ReadonlyArray<WbEnvelopeVertex>,
  weightLb: number,
): WbEnvelopeLimits | null {
  if (envelope.length < 2) return null;
  const weights = envelope.map((v) => v.weightLb);
  const minWeightLb = Math.min(...weights);
  const maxWeightLb = Math.max(...weights);
  const xs: number[] = [];
  for (let i = 0; i < envelope.length; i += 1) {
    const a = envelope[i]!;
    const b = envelope[(i + 1) % envelope.length]!;
    const dy = b.weightLb - a.weightLb;
    if (dy === 0) {
      if (Math.abs(a.weightLb - weightLb) < 1e-6) {
        xs.push(a.pctMac, b.pctMac);
      }
      continue;
    }
    const t = (weightLb - a.weightLb) / dy;
    if (t >= -1e-9 && t <= 1 + 1e-9) {
      xs.push(a.pctMac + t * (b.pctMac - a.pctMac));
    }
  }
  if (!xs.length) {
    return { fwdPctMac: NaN, aftPctMac: NaN, minWeightLb, maxWeightLb };
  }
  return {
    fwdPctMac: Math.min(...xs),
    aftPctMac: Math.max(...xs),
    minWeightLb,
    maxWeightLb,
  };
}

export function finiteNumber(value: unknown, fallback: number): number {
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function finiteNonNeg(value: unknown, fallback: number): number {
  return Math.max(0, finiteNumber(value, fallback));
}

export function evaluatePointReasons(input: {
  kind: WbPointKind;
  label: string;
  weightLb: number;
  pctMac: number;
  limits: Pick<WbLimits, "mzfwLb" | "mtowLb" | "mlwLb" | "mrwLb">;
  envelope: ReadonlyArray<WbEnvelopeVertex>;
}): string[] {
  const reasons: string[] = [];
  const w = input.weightLb;
  const pct = input.pctMac;
  const { kind, label, limits, envelope } = input;

  if (kind === "zfw" && w > limits.mzfwLb + 0.5) {
    reasons.push(`${label} ${formatLb(w)} is above MZFW ${formatLb(limits.mzfwLb)}`);
  }
  if (kind === "ramp" && w > limits.mrwLb + 0.5) {
    reasons.push(`${label} ${formatLb(w)} is above MRW ${formatLb(limits.mrwLb)}`);
  }
  if (kind === "tow" && w > limits.mtowLb + 0.5) {
    reasons.push(`${label} ${formatLb(w)} is above MTOW ${formatLb(limits.mtowLb)}`);
  }
  if (kind === "ldg" && w > limits.mlwLb + 0.5) {
    reasons.push(`${label} ${formatLb(w)} is above MLW ${formatLb(limits.mlwLb)}`);
  }

  const band = envelopeLimitsAtWeight(envelope, w);
  if (!band || !Number.isFinite(band.fwdPctMac) || !Number.isFinite(band.aftPctMac)) {
    if (envelope.length >= 3) {
      const minW = band?.minWeightLb ?? Math.min(...envelope.map((v) => v.weightLb));
      const maxW = band?.maxWeightLb ?? Math.max(...envelope.map((v) => v.weightLb));
      if (w + 0.5 < minW) {
        reasons.push(`${label} ${formatLb(w)} is below the envelope weight range (${formatLb(minW)}–${formatLb(maxW)})`);
      } else if (w > maxW + 0.5) {
        reasons.push(`${label} ${formatLb(w)} is above the envelope weight range (${formatLb(minW)}–${formatLb(maxW)})`);
      } else {
        reasons.push(`${label} ${formatPctMac(pct)} at ${formatLb(w)} is outside the CG envelope`);
      }
    }
    return uniqueStrings(reasons);
  }

  if (pct + 0.05 < band.fwdPctMac) {
    reasons.push(
      `${label} CG ${formatPctMac(pct)} is forward of the forward limit ${formatPctMac(band.fwdPctMac)} at ${formatLb(w)}`,
    );
  } else if (pct > band.aftPctMac + 0.05) {
    reasons.push(
      `${label} CG ${formatPctMac(pct)} is aft of the aft limit ${formatPctMac(band.aftPctMac)} at ${formatLb(w)}`,
    );
  } else if (!pointInEnvelope(envelope, w, pct)) {
    reasons.push(`${label} ${formatPctMac(pct)} at ${formatLb(w)} is outside the CG envelope`);
  }

  return uniqueStrings(reasons);
}

function uniqueStrings(values: string[]): string[] {
  return [...new Set(values.filter(Boolean))];
}

function makePoint(
  kind: WbPointKind,
  label: string,
  weightLb: number,
  momentInLb: number,
  limits: WbLimits,
  envelope: ReadonlyArray<WbEnvelopeVertex>,
): WbComputedPoint {
  const armIn = cgArmIn(weightLb, momentInLb);
  const pctMac = pctMacFromArm(armIn, limits.lemacStaIn, limits.macIn);
  const reasons = evaluatePointReasons({ kind, label, weightLb, pctMac, limits, envelope });
  return {
    kind,
    label,
    weightLb,
    momentInLb,
    armIn,
    pctMac,
    ok: reasons.length === 0,
    reasons,
  };
}

export function computeWeightBalance(profile: WbProfile): WbResult {
  const limits = profile.limits;
  const occupied = profile.seats.filter((s) => s.occupied).map((s) => ({
    weightLb: finiteNonNeg(s.weightLb, s.defaultWeightLb),
    armIn: finiteNumber(s.armIn, 0),
  }));
  const cargo = profile.cargo.map((c) => ({
    weightLb: finiteNonNeg(c.weightLb, 0),
    armIn: finiteNumber(c.armIn, 0),
  }));
  const tanks = profile.tanks.map((t) => ({
    ...t,
    quantityLb: Math.min(finiteNonNeg(t.quantityLb, 0), finiteNonNeg(t.capacityLb, t.quantityLb)),
    armIn: finiteNumber(t.armIn, 0),
  }));

  const extraReasons: string[] = [];
  const tankTotal = tanks.reduce((sum, t) => sum + t.quantityLb, 0);
  if (tankTotal > limits.maxFuelLb + 0.5) {
    extraReasons.push(`Fuel on board ${formatLb(tankTotal)} is above max fuel ${formatLb(limits.maxFuelLb)}`);
  }
  for (const tank of profile.tanks) {
    const qty = finiteNonNeg(tank.quantityLb, 0);
    const cap = finiteNonNeg(tank.capacityLb, 0);
    if (qty > cap + 0.5) {
      extraReasons.push(`${tank.label} ${formatLb(qty)} is above tank capacity ${formatLb(cap)}`);
    }
  }

  const zfwItems = [{ weightLb: limits.bowLb, armIn: limits.bowArmIn }, ...occupied, ...cargo];
  const zfwTotals = sumMoments(zfwItems);
  const payloadLb = Math.max(0, zfwTotals.weightLb - limits.bowLb);
  if (payloadLb > limits.maxPayloadLb + 0.5) {
    extraReasons.push(`Payload ${formatLb(payloadLb)} is above max payload ${formatLb(limits.maxPayloadLb)}`);
  }

  const fuelItems = tanks.map((t) => ({ weightLb: t.quantityLb, armIn: t.armIn }));
  const fuelTotals = sumMoments(fuelItems);
  const fuelOnBoardLb = fuelTotals.weightLb;
  const taxi = Math.min(finiteNonNeg(limits.taxiFuelLb, 0), fuelOnBoardLb);
  const trip = Math.min(finiteNonNeg(profile.tripFuelLb, 0), Math.max(0, fuelOnBoardLb - taxi));
  const takeoffFuelLb = Math.max(0, fuelOnBoardLb - taxi);
  const fuelRemainingLb = Math.max(0, takeoffFuelLb - trip);

  const taxiFraction = fuelOnBoardLb > 0 ? taxi / fuelOnBoardLb : 0;
  const remFraction = fuelOnBoardLb > 0 ? fuelRemainingLb / fuelOnBoardLb : 0;

  const rampWeight = zfwTotals.weightLb + fuelOnBoardLb;
  const rampMoment = zfwTotals.momentInLb + fuelTotals.momentInLb;
  const towWeight = zfwTotals.weightLb + takeoffFuelLb;
  const towMoment = zfwTotals.momentInLb + fuelTotals.momentInLb * (1 - taxiFraction);
  const ldgWeight = zfwTotals.weightLb + fuelRemainingLb;
  const ldgMoment = zfwTotals.momentInLb + fuelTotals.momentInLb * remFraction;

  const zfw = makePoint("zfw", "ZFW", zfwTotals.weightLb, zfwTotals.momentInLb, limits, profile.envelope);
  const ramp = makePoint("ramp", "Ramp weight", rampWeight, rampMoment, limits, profile.envelope);
  const tow = makePoint("tow", "Takeoff weight", towWeight, towMoment, limits, profile.envelope);
  const ldg = makePoint("ldg", "Landing weight", ldgWeight, ldgMoment, limits, profile.envelope);

  const reasons = uniqueStrings([
    ...extraReasons,
    ...zfw.reasons,
    ...ramp.reasons,
    ...tow.reasons,
    ...ldg.reasons,
  ]);

  return {
    zfw,
    ramp,
    tow,
    ldg,
    payloadLb,
    fuelOnBoardLb,
    takeoffFuelLb,
    fuelRemainingLb,
    withinLimits: reasons.length === 0,
    reasons,
  };
}

function seat(id: string, label: string, armIn: number, defaultWeightLb = WB_DEFAULT_PAX_LB): WbSeat {
  return { id, label, armIn, weightLb: defaultWeightLb, defaultWeightLb, occupied: false };
}

function cargo(id: string, label: string, armIn: number): WbStation {
  return { id, label, armIn, weightLb: 0 };
}

function tank(id: string, label: string, armIn: number, capacityLb: number, quantityLb = 0): WbTank {
  return { id, label, armIn, capacityLb, quantityLb };
}

export function hawker800xpDefaultProfile(
  spec: Pick<AircraftSpec, "id" | "icaoType"> & Partial<Pick<AircraftSpec, "fuelDensityLbPerGal">>,
): WbProfile {
  const bowArmIn = hawkerBowArmIn();
  const taxiFuelLb = Math.round((HAWKER.taxiFuelGal ?? 40) * fuelDensityForSpec(spec));
  return {
    aircraftId: spec.id,
    icaoType: spec.icaoType || HAWKER_800XP.icaoType,
    sourceLabel: "Hawker 800XP desk training defaults (Emerson weights; classic envelope)",
    limits: {
      bowLb: HAWKER_800XP.bowLb,
      bowArmIn,
      mzfwLb: HAWKER_800XP.mzfwLb,
      mtowLb: HAWKER_800XP.mtowLb,
      mlwLb: HAWKER_800XP.mlwLb,
      mrwLb: HAWKER_800XP.mrwLb,
      maxFuelLb: HAWKER_800XP.maxFuelLb,
      ventralFuelLb: HAWKER_800XP.ventralFuelLb,
      maxPayloadLb: HAWKER_800XP.maxPayloadLb,
      payloadFullFuelLb: HAWKER_800XP.payloadFullFuelLb,
      macIn: HAWKER_800XP_MAC_IN,
      lemacStaIn: HAWKER_800XP_LEMAC_STA_IN,
      taxiFuelLb,
    },
    envelope: HAWKER_800XP_WB_ENVELOPE.map((v) => ({ ...v })),
    seats: [
      seat("pax-1l", "Seat 1 L", 210),
      seat("pax-1r", "Seat 1 R", 210),
      seat("pax-2l", "Seat 2 L", 245),
      seat("pax-2r", "Seat 2 R", 245),
      seat("pax-3l", "Seat 3 L", 285),
      seat("pax-3r", "Seat 3 R", 285),
      seat("pax-4l", "Seat 4 L", 320),
      seat("pax-4r", "Seat 4 R", 320),
    ],
    cargo: [
      cargo("fwd-closet", "Forward closet", 190),
      cargo("aft-bag", "Aft baggage", 430),
    ],
    tanks: [
      tank("fuel-l", "Left wing", 372, HAWKER_WING_CAP_LB, 2500),
      tank("fuel-r", "Right wing", 372, HAWKER_WING_CAP_LB, 2500),
      tank("fuel-v", "Ventral", 410, HAWKER_800XP.ventralFuelLb, 0),
    ],
    tripFuelLb: 2000,
  };
}

export function pc12DefaultProfile(
  spec: Pick<AircraftSpec, "id" | "icaoType" | "oewLb" | "mzfwLb" | "mtowLb" | "mlwLb" | "maxFuelLb" | "maxPax" | "maxPayloadLb" | "taxiFuelGal"> &
    Partial<Pick<AircraftSpec, "fuelDensityLbPerGal">>,
): WbProfile {
  const bowLb = finiteNumber(spec.oewLb, PC12.oewLb);
  const mtow = finiteNumber(spec.mtowLb, PC12.mtowLb);
  const mzfw = finiteNumber(spec.mzfwLb, PC12.mzfwLb);
  const mlw = finiteNumber(spec.mlwLb, PC12.mlwLb);
  const maxFuel = finiteNumber(spec.maxFuelLb, PC12.maxFuelLb);
  const macIn = 70;
  const lemacStaIn = 180;
  const bowArmIn = armFromPctMac(26, lemacStaIn, macIn);
  const pax = Math.max(1, spec.maxPax || 8);
  const seats: WbSeat[] = [];
  for (let i = 0; i < pax; i += 1) {
    const row = Math.floor(i / 2);
    const side = i % 2 === 0 ? "L" : "R";
    seats.push(seat(`pax-${i + 1}${side.toLowerCase()}`, `Seat ${i + 1} ${side}`, 145 + row * 28));
  }
  return {
    aircraftId: spec.id,
    icaoType: spec.icaoType || PC12.icaoType,
    sourceLabel: "PC-12 NG desk training defaults (editable)",
    limits: {
      bowLb,
      bowArmIn,
      mzfwLb: mzfw,
      mtowLb: mtow,
      mlwLb: mlw,
      mrwLb: mtow + 50,
      maxFuelLb: maxFuel,
      ventralFuelLb: 0,
      maxPayloadLb: spec.maxPayloadLb ?? Math.max(0, mzfw - bowLb),
      payloadFullFuelLb: Math.max(0, mtow - bowLb - maxFuel),
      macIn,
      lemacStaIn,
      taxiFuelLb: Math.round((spec.taxiFuelGal ?? PC12.taxiFuelGal) * fuelDensityForSpec(spec)),
    },
    envelope: genericEnvelope(bowLb, mtow + 50),
    seats,
    cargo: [cargo("cabin-bag", "Cabin baggage", 200), cargo("aft-bag", "Aft cargo", 280)],
    tanks: [tank("fuel-main", "Main fuel", armFromPctMac(27, lemacStaIn, macIn), maxFuel, Math.min(1200, maxFuel))],
    tripFuelLb: 600,
  };
}

export function genericDefaultProfile(spec: AircraftSpec): WbProfile {
  const bowLb = finiteNumber(spec.oewLb, 0);
  const mtow = finiteNumber(spec.mtowLb, 0);
  const mzfw = finiteNumber(spec.mzfwLb, 0);
  const mlw = finiteNumber(spec.mlwLb, 0);
  const maxFuel = finiteNumber(spec.maxFuelLb, 0);
  const macIn = 80;
  const lemacStaIn = 200;
  const bowArmIn = armFromPctMac(25, lemacStaIn, macIn);
  const pax = Math.max(0, spec.maxPax || 0);
  const seats: WbSeat[] = [];
  for (let i = 0; i < pax; i += 1) {
    seats.push(seat(`pax-${i + 1}`, `Seat ${i + 1}`, 160 + i * 22));
  }
  return {
    aircraftId: spec.id,
    icaoType: spec.icaoType || "ZZZZ",
    sourceLabel: "Generic editable W&B template",
    limits: {
      bowLb,
      bowArmIn,
      mzfwLb: mzfw,
      mtowLb: mtow,
      mlwLb: mlw,
      mrwLb: spec.mrwLb ?? mtow,
      maxFuelLb: maxFuel,
      ventralFuelLb: 0,
      maxPayloadLb: spec.maxPayloadLb ?? Math.max(0, mzfw - bowLb),
      payloadFullFuelLb: Math.max(0, mtow - bowLb - maxFuel),
      macIn,
      lemacStaIn,
      taxiFuelLb: Math.round((spec.taxiFuelGal ?? 0) * fuelDensityForSpec(spec)),
    },
    envelope: genericEnvelope(Math.max(1, bowLb * 0.85), Math.max(bowLb + 100, (spec.mrwLb ?? mtow) || bowLb + 100)),
    seats,
    cargo: [cargo("cargo-1", "Cargo / baggage", 220)],
    tanks: [tank("fuel-1", "Fuel", armFromPctMac(26, lemacStaIn, macIn), maxFuel, 0)],
    tripFuelLb: 0,
  };
}

export function genericEnvelope(minWeightLb: number, maxWeightLb: number): WbEnvelopeVertex[] {
  const lo = Math.max(1, Math.round(minWeightLb));
  const hi = Math.max(lo + 100, Math.round(maxWeightLb));
  const mid = Math.round((lo + hi) / 2);
  return [
    { weightLb: lo, pctMac: 16.0 },
    { weightLb: mid, pctMac: 17.0 },
    { weightLb: hi, pctMac: 19.0 },
    { weightLb: hi, pctMac: 32.0 },
    { weightLb: mid, pctMac: 33.0 },
    { weightLb: lo, pctMac: 33.0 },
  ];
}

export function typeDefaultProfile(spec: AircraftSpec): WbProfile {
  if (isHawker800xpType(spec.icaoType)) return hawker800xpDefaultProfile(spec);
  if ((spec.icaoType ?? "").toUpperCase() === "PC12") return pc12DefaultProfile(spec);
  return genericDefaultProfile(spec);
}

function hydrateSeat(raw: Partial<WbSeat> | undefined, fallback: WbSeat): WbSeat {
  return {
    id: raw?.id?.trim() || fallback.id,
    label: raw?.label?.trim() || fallback.label,
    armIn: finiteNumber(raw?.armIn, fallback.armIn),
    weightLb: finiteNonNeg(raw?.weightLb, fallback.weightLb),
    defaultWeightLb: finiteNonNeg(raw?.defaultWeightLb, fallback.defaultWeightLb),
    occupied: Boolean(raw?.occupied),
  };
}

function hydrateStation(raw: Partial<WbStation> | undefined, fallback: WbStation): WbStation {
  return {
    id: raw?.id?.trim() || fallback.id,
    label: raw?.label?.trim() || fallback.label,
    armIn: finiteNumber(raw?.armIn, fallback.armIn),
    weightLb: finiteNonNeg(raw?.weightLb, fallback.weightLb),
  };
}

function hydrateTank(raw: Partial<WbTank> | undefined, fallback: WbTank): WbTank {
  return {
    id: raw?.id?.trim() || fallback.id,
    label: raw?.label?.trim() || fallback.label,
    armIn: finiteNumber(raw?.armIn, fallback.armIn),
    capacityLb: finiteNonNeg(raw?.capacityLb, fallback.capacityLb),
    quantityLb: finiteNonNeg(raw?.quantityLb, fallback.quantityLb),
  };
}

function hydrateEnvelope(raw: unknown, fallback: WbEnvelopeVertex[]): WbEnvelopeVertex[] {
  if (!Array.isArray(raw) || raw.length < 3) return fallback.map((v) => ({ ...v }));
  const verts = raw
    .map((row) => {
      const r = row as Partial<WbEnvelopeVertex>;
      return {
        weightLb: finiteNumber(r.weightLb, NaN),
        pctMac: finiteNumber(r.pctMac, NaN),
      };
    })
    .filter((v) => Number.isFinite(v.weightLb) && Number.isFinite(v.pctMac));
  return verts.length >= 3 ? verts : fallback.map((v) => ({ ...v }));
}

export function hydrateWeightBalanceProfile(raw: Partial<WbProfile> | null | undefined, spec: AircraftSpec): WbProfile {
  const base = typeDefaultProfile(spec);
  if (!raw) return base;
  const limitsRaw = raw.limits ?? (raw as Partial<WbLimits>);
  const limits: WbLimits = {
    bowLb: finiteNumber(limitsRaw.bowLb, base.limits.bowLb),
    bowArmIn: finiteNumber(limitsRaw.bowArmIn, base.limits.bowArmIn),
    mzfwLb: finiteNumber(limitsRaw.mzfwLb, base.limits.mzfwLb),
    mtowLb: finiteNumber(limitsRaw.mtowLb, base.limits.mtowLb),
    mlwLb: finiteNumber(limitsRaw.mlwLb, base.limits.mlwLb),
    mrwLb: finiteNumber(limitsRaw.mrwLb, base.limits.mrwLb),
    maxFuelLb: finiteNumber(limitsRaw.maxFuelLb, base.limits.maxFuelLb),
    ventralFuelLb: finiteNumber(limitsRaw.ventralFuelLb, base.limits.ventralFuelLb),
    maxPayloadLb: finiteNumber(limitsRaw.maxPayloadLb, base.limits.maxPayloadLb),
    payloadFullFuelLb: finiteNumber(limitsRaw.payloadFullFuelLb, base.limits.payloadFullFuelLb),
    macIn: finiteNumber(limitsRaw.macIn, base.limits.macIn),
    lemacStaIn: finiteNumber(limitsRaw.lemacStaIn, base.limits.lemacStaIn),
    taxiFuelLb: finiteNonNeg(limitsRaw.taxiFuelLb, base.limits.taxiFuelLb),
  };
  const seats = Array.isArray(raw.seats) && raw.seats.length
    ? raw.seats.map((s, i) => hydrateSeat(s, base.seats[i] ?? seat(`pax-${i + 1}`, `Seat ${i + 1}`, 200)))
    : base.seats;
  const cargoRows = Array.isArray(raw.cargo) && raw.cargo.length
    ? raw.cargo.map((c, i) => hydrateStation(c, base.cargo[i] ?? cargo(`cargo-${i + 1}`, `Cargo ${i + 1}`, 220)))
    : base.cargo;
  const tanks = Array.isArray(raw.tanks) && raw.tanks.length
    ? raw.tanks.map((t, i) => hydrateTank(t, base.tanks[i] ?? tank(`fuel-${i + 1}`, `Fuel ${i + 1}`, limits.bowArmIn, limits.maxFuelLb)))
    : base.tanks;
  return {
    aircraftId: raw.aircraftId?.trim() || spec.id,
    icaoType: raw.icaoType?.trim() || spec.icaoType,
    sourceLabel: raw.sourceLabel?.trim() || base.sourceLabel,
    limits,
    envelope: hydrateEnvelope(raw.envelope, base.envelope),
    seats,
    cargo: cargoRows,
    tanks,
    tripFuelLb: finiteNonNeg(raw.tripFuelLb, base.tripFuelLb),
    ...(typeof raw.loadFlagNote === "string" && raw.loadFlagNote.trim()
      ? { loadFlagNote: raw.loadFlagNote.trim().slice(0, 400) }
      : {}),
  };
}

export function hydrateWeightBalanceDesk(raw?: Partial<WeightBalanceDesk> | null): WeightBalanceDesk {
  const profiles: Record<string, WbProfile> = {};
  const incoming = raw?.profiles && typeof raw.profiles === "object" ? raw.profiles : {};
  for (const [id, profile] of Object.entries(incoming)) {
    if (!profile || typeof profile !== "object") continue;
    const icao = (profile as WbProfile).icaoType || "";
    const stub: AircraftSpec = {
      ...genericStub(id, icao),
      id,
      icaoType: icao || (isHawker800xpType(icao) ? "H25B" : icao),
    };
    profiles[id] = hydrateWeightBalanceProfile(profile as Partial<WbProfile>, stub);
  }
  return {
    selectedAircraftId: typeof raw?.selectedAircraftId === "string" ? raw.selectedAircraftId : "",
    profiles,
  };
}

function genericStub(id: string, icaoType: string): AircraftSpec {
  if (isHawker800xpType(icaoType) || id.includes("hawker")) return { ...HAWKER, id, icaoType: icaoType || HAWKER.icaoType };
  if (icaoType.toUpperCase() === "PC12" || id.includes("pc12")) return { ...PC12, id, icaoType: icaoType || PC12.icaoType };
  return blankAircraft({ id, icaoType: icaoType || "ZZZZ", name: "Airframe" });
}

/**
 * Structural limits that also live on the fleet tail. A saved profile keeps
 * its stations and its BOW arm. MZFW, MLW, MTOW, fuel, ramp, and payload
 * follow the tail whenever the tail has a number, so Fleet and W&B cannot drift.
 */
/** BOW is the saved operating weight. It is light when it sits under the fleet empty weight. */
export function bowBelowFleetOew(bowLb: number, oewLb: number): boolean {
  return oewLb > 0 && bowLb < oewLb;
}

export function overlayLiveFleetLimits(profile: WbProfile, spec: AircraftSpec): WbProfile {
  const limits = { ...profile.limits };
  if (finiteNumber(spec.mzfwLb, 0) > 0) limits.mzfwLb = spec.mzfwLb;
  if (finiteNumber(spec.mlwLb, 0) > 0) limits.mlwLb = spec.mlwLb;
  if (finiteNumber(spec.mtowLb, 0) > 0) limits.mtowLb = spec.mtowLb;
  if (finiteNumber(spec.maxFuelLb, 0) > 0) limits.maxFuelLb = spec.maxFuelLb;
  if (finiteNumber(spec.mrwLb, 0) > 0) limits.mrwLb = spec.mrwLb as number;
  if (finiteNumber(spec.maxPayloadLb, 0) > 0) limits.maxPayloadLb = spec.maxPayloadLb as number;
  return {
    ...profile,
    aircraftId: spec.id || profile.aircraftId,
    icaoType: spec.icaoType || profile.icaoType,
    limits,
  };
}

export function profileForTail(desk: WeightBalanceDesk, spec: AircraftSpec): WbProfile {
  const saved = desk.profiles[spec.id];
  const base = saved ? hydrateWeightBalanceProfile(saved, spec) : typeDefaultProfile(spec);
  return overlayLiveFleetLimits(base, spec);
}

export function upsertWeightBalanceProfile(desk: WeightBalanceDesk, profile: WbProfile): WeightBalanceDesk {
  return {
    selectedAircraftId: desk.selectedAircraftId || profile.aircraftId,
    profiles: { ...desk.profiles, [profile.aircraftId]: profile },
  };
}

export function selectWeightBalanceTail(desk: WeightBalanceDesk, aircraftId: string): WeightBalanceDesk {
  return { ...desk, selectedAircraftId: aircraftId };
}

export function resolveSelectedAircraftId(desk: WeightBalanceDesk, fleet: AircraftSpec[]): string {
  if (fleet.some((a) => a.id === desk.selectedAircraftId)) return desk.selectedAircraftId;
  return fleet[0]?.id ?? "";
}

export function wbStatusLabel(result: WbResult): "Within limits" | "Out of envelope" {
  return result.withinLimits ? "Within limits" : "Out of envelope";
}
